import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, Tab } from '@headlessui/react'
import { X } from 'lucide-react'
import { useInfiniteQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import {
  BusinessMemoryItemFieldsFragment,
  BusinessMemoryLayer,
  ListBusinessMemoriesDocument,
  ListBusinessMemoriesQuery,
  ListBusinessMemoriesQueryVariables,
} from '__generated__/api-types-and-hooks'
import { fetcher } from 'lib/api-client'
import { AppCoachMemoryItem } from 'components/Common/AppCoachMemory/AppCoachMemoryItem.component'
import {
  explainedMessage,
  isPermanentError,
  retryUnlessRefused,
} from 'components/Common/AppCoachMemory/coachMemoryErrors'

// Group order; kinds this screen doesn't know yet go last, under "Other notes"
const KIND_ORDER = [
  'fact',
  'preference',
  'constraint',
  'decision',
  'commitment',
  'open_loop',
  'feedback',
  'episode_summary',
]

const groupByKind = (items: BusinessMemoryItemFieldsFragment[]) => {
  const groups = new Map<string, BusinessMemoryItemFieldsFragment[]>()
  items.forEach((item) => {
    const kind = KIND_ORDER.includes(item.kind) ? item.kind : 'other'
    groups.set(kind, [...(groups.get(kind) ?? []), item])
  })
  return [...KIND_ORDER, 'other']
    .filter((kind) => groups.has(kind))
    .map((kind) => ({
      kind,
      // Pinned notes first, otherwise in the order the API sent them
      items: (groups.get(kind) as BusinessMemoryItemFieldsFragment[])
        .map((item, index) => ({ item, index }))
        .sort((a, b) => Number(b.item.pinned) - Number(a.item.pinned) || a.index - b.index)
        .map(({ item }) => item),
    }))
}

interface AppCoachMemoryProps {
  // The client's business, for advisors; owners and team members leave it out (their own business)
  businessId?: string
  onClose: () => void
}

/** "What Coach remembers": the business memory, grouped by kind, with edit / pin / delete. */
export const AppCoachMemory: React.FC<AppCoachMemoryProps> = ({ businessId, onClose }) => {
  const { t } = useTranslation()
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  // The list, focused after a delete (the deleted card took the focus with it)
  const listRef = useRef<HTMLDivElement>(null)
  const [layer, setLayer] = useState<BusinessMemoryLayer>(BusinessMemoryLayer.Shared)
  // Layers the caller may see, from the last answer (kept while another tab loads)
  const [lastLayers, setLastLayers] = useState<BusinessMemoryLayer[] | null>(null)
  // Deleted here: hidden until the reloaded list leaves them out
  const [removedIds, setRemovedIds] = useState<string[]>([])
  const [deletions, setDeletions] = useState(0)
  useEffect(() => {
    if (deletions) listRef.current?.focus()
  }, [deletions])
  // A save or pin found the note deleted by someone else: why, and the draft that was not saved
  const [gone, setGone] = useState<{ draft?: string } | null>(null)

  const {
    data,
    error,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<ListBusinessMemoriesQuery>(
    ['listBusinessMemories.infinite', { businessId, layer }],
    ({ pageParam }) =>
      fetcher<ListBusinessMemoriesQuery, ListBusinessMemoriesQueryVariables>(
        ListBusinessMemoriesDocument,
        {
          input: {
            ...(businessId && { businessId }),
            layers: [layer],
            ...(pageParam && { nextToken: pageParam }),
          },
        }
      )(),
    {
      getNextPageParam: (lastPage) => lastPage.listBusinessMemories.nextToken || undefined,
      refetchOnWindowFocus: false,
      retry: retryUnlessRefused,
    }
  )

  // A focused 'Try again' or 'Show more' button goes away once the list loads: the list takes the
  // focus, or it would drop to the page and the next Tab would start over at the top.
  const retry = () => refetch().then(() => listRef.current?.focus())
  const showMore = () => {
    // aria-disabled, not disabled, while loading: a disabled button would drop the focus
    if (isFetchingNextPage) return
    fetchNextPage().then((result) => {
      if (!result.hasNextPage) listRef.current?.focus()
    })
  }

  const firstPage = data?.pages[0]?.listBusinessMemories
  useEffect(() => {
    if (firstPage?.layers) setLastLayers(firstPage.layers)
  }, [firstPage?.layers])
  // Unknown until the first answer: the notice for owners must not show to an advisor meanwhile
  const layers = firstPage?.layers ?? lastLayers

  const groups = useMemo(
    () =>
      groupByKind(
        (data?.pages ?? [])
          .flatMap((page) => page.listBusinessMemories.items)
          .filter((item) => !removedIds.includes(item.memoryId))
      ),
    [data, removedIds]
  )

  const showHeader = !!layers && firstPage?.enabled !== false
  const hasAdvisorTab = showHeader && !!layers?.includes(BusinessMemoryLayer.Advisor)
  const notice = !hasAdvisorTab
    ? t('askCL.memory.notice.shared')
    : layer === BusinessMemoryLayer.Advisor
    ? t('askCL.memory.notice.advisorOnly')
    : t('askCL.memory.notice.advisorShared')
  const tabs = [
    { layer: BusinessMemoryLayer.Shared, label: t('askCL.memory.tabs.shared') },
    { layer: BusinessMemoryLayer.Advisor, label: t('askCL.memory.tabs.advisor') },
  ]
  const selectedTab = Math.max(
    tabs.findIndex((tab) => tab.layer === layer),
    0
  )
  const listClass = 'flex-1 overflow-y-auto p-4 focus:outline-none'

  const renderBody = () => {
    if (isLoading) {
      return (
        <div aria-busy="true" className="space-y-3">
          <span className="sr-only">{t('askCL.memory.loading')}</span>
          {[0, 1, 2].map((row) => (
            <div
              key={row}
              data-testid="coach-memory-skeleton"
              className="animate-pulse rounded-lg border border-app-grey-25 p-3"
            >
              <div className="h-3 w-3/4 rounded bg-app-grey-20" />
              <div className="mt-2 h-3 w-1/2 rounded bg-app-grey-20" />
            </div>
          ))}
        </div>
      )
    }
    // A refusal (e.g. no access any more) replaces the notes it was refused for
    if (!firstPage || (isError && isPermanentError(error))) {
      return (
        <div role="alert" className="rounded-lg bg-app-red-10 p-4 text-sm text-app-red-80">
          {/* The API's reason when it gave one (e.g. no access to this business) */}
          <p>{explainedMessage(error) || t('askCL.memory.errors.load')}</p>
          {!isPermanentError(error) && (
            <button
              type="button"
              onClick={retry}
              className="mt-2 font-medium underline focus:outline-none focus-visible:ring-2 focus-visible:ring-app-primary-125"
            >
              {t('askCL.memory.actions.retry')}
            </button>
          )}
        </div>
      )
    }
    if (!firstPage.enabled) {
      return (
        <div className="rounded-lg border border-app-grey-25 bg-app-grey-5 p-4 text-center">
          <p className="text-sm font-semibold text-app-grey-100">
            {t('askCL.memory.disabled.title')}
          </p>
          <p className="mt-1 text-sm text-app-grey-75">{t('askCL.memory.disabled.body')}</p>
        </div>
      )
    }
    if (!groups.length) {
      return (
        <p className="rounded-lg border border-dashed border-app-grey-25 p-6 text-center text-sm text-app-grey-75">
          {layer === BusinessMemoryLayer.Advisor
            ? t('askCL.memory.emptyAdvisor')
            : t('askCL.memory.empty')}
        </p>
      )
    }
    return (
      <div className="space-y-5">
        {groups.map((group) => (
          <section key={group.kind} aria-labelledby={`coach-memory-kind-${group.kind}`}>
            <h3
              id={`coach-memory-kind-${group.kind}`}
              className="mb-2 text-sm font-semibold text-app-grey-100"
            >
              {t(`askCL.memory.kinds.${group.kind}`)}
            </h3>
            <ul className="list-none space-y-2">
              {group.items.map((item) => (
                <AppCoachMemoryItem
                  key={item.memoryId}
                  item={item}
                  businessId={businessId}
                  onChanged={() => refetch()}
                  onDeleted={(memoryId) => {
                    setRemovedIds((ids) => [...ids, memoryId])
                    setDeletions((count) => count + 1)
                  }}
                  onGone={(memoryId, draft) => {
                    setGone({ draft })
                    setRemovedIds((ids) => [...ids, memoryId])
                    setDeletions((count) => count + 1)
                    refetch()
                  }}
                />
              ))}
            </ul>
          </section>
        ))}
        {isError && (
          <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-app-red-80">
            <span>{explainedMessage(error) || t('askCL.memory.errors.load')}</span>
            {!isPermanentError(error) && (
              <button
                type="button"
                onClick={retry}
                className="font-medium underline focus:outline-none focus-visible:ring-2 focus-visible:ring-app-primary-125"
              >
                {t('askCL.memory.actions.retry')}
              </button>
            )}
          </div>
        )}
        {hasNextPage && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={showMore}
              aria-disabled={isFetchingNextPage || undefined}
              className="rounded-md border border-app-grey-25 px-4 py-2 text-sm font-medium text-app-grey-100 hover:bg-app-grey-5 aria-disabled:opacity-50 aria-disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-app-primary-125"
            >
              {isFetchingNextPage ? t('askCL.memory.loading') : t('askCL.memory.actions.showMore')}
            </button>
          </div>
        )}
      </div>
    )
  }

  // Stays through the reload that removes the note (the card itself is gone with it)
  const goneNotice = (
    <div role="status">
      {gone && (
        <div className="mb-3 rounded-lg border border-app-grey-25 bg-app-grey-5 p-3 text-sm text-app-grey-100">
          <p>{t('askCL.memory.deletedElsewhere.body')}</p>
          {gone.draft && (
            <>
              <p className="mt-2 text-xs text-app-grey-75">
                {t('askCL.memory.deletedElsewhere.draft')}
              </p>
              <p className="mt-1 rounded-md border border-app-grey-25 bg-white p-2 whitespace-pre-wrap break-words">
                {gone.draft}
              </p>
            </>
          )}
          <button
            type="button"
            onClick={() => {
              setGone(null)
              listRef.current?.focus()
            }}
            className="mt-2 font-medium underline focus:outline-none focus-visible:ring-2 focus-visible:ring-app-primary-125"
          >
            {t('askCL.memory.actions.dismiss')}
          </button>
        </div>
      )}
    </div>
  )

  return (
    <Dialog
      open
      as="div"
      className="relative z-[60]"
      initialFocus={closeButtonRef}
      onClose={onClose}
    >
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
          <div className="flex items-start justify-between gap-4 bg-app-primary-125 p-4 text-white">
            <div>
              <Dialog.Title as="h2" className="text-base font-semibold text-white">
                {t('askCL.memory.title')}
              </Dialog.Title>
              <Dialog.Description className="text-xs text-white opacity-90">
                {t('askCL.memory.subtitle')}
              </Dialog.Description>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              aria-label={t('askCL.memory.actions.close')}
              className="rounded p-1 transition-colors hover:bg-white hover:bg-opacity-20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          {hasAdvisorTab ? (
            // Headless UI tabs: arrow / Home / End keys, one tab stop, a labelled panel
            <Tab.Group
              selectedIndex={selectedTab}
              onChange={(index) => setLayer(tabs[index].layer)}
            >
              <div className="space-y-3 border-b border-app-grey-25 bg-app-grey-5 px-4 py-3">
                <Tab.List
                  aria-label={t('askCL.memory.tabs.label')}
                  className="flex rounded-lg bg-white p-1 shadow-sm"
                >
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.layer}
                      className={({ selected }) =>
                        `flex-1 rounded-md px-3 py-2 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-app-primary-125 ${
                          selected
                            ? 'font-semibold bg-app-white-95 text-app-grey-100'
                            : 'font-medium text-app-grey-65 hover:opacity-80'
                        }`
                      }
                    >
                      {tab.label}
                    </Tab>
                  ))}
                </Tab.List>
                <p className="text-xs text-app-grey-75">{notice}</p>
              </div>
              <Tab.Panels as={React.Fragment}>
                {tabs.map((tab, index) => (
                  <Tab.Panel
                    key={tab.layer}
                    ref={index === selectedTab ? listRef : undefined}
                    className={listClass}
                    data-scrollable
                    data-testid={index === selectedTab ? 'coach-memory-list' : undefined}
                  >
                    {index === selectedTab && (
                      <>
                        {goneNotice}
                        {renderBody()}
                      </>
                    )}
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>
          ) : (
            <>
              {showHeader && (
                <div className="border-b border-app-grey-25 bg-app-grey-5 px-4 py-3">
                  <p className="text-xs text-app-grey-75">{notice}</p>
                </div>
              )}
              <div
                ref={listRef}
                tabIndex={-1}
                className={listClass}
                data-scrollable
                data-testid="coach-memory-list"
              >
                {goneNotice}
                {renderBody()}
              </div>
            </>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}
